import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { Button, ButtonGroup, CardView, rem, SettingsItem } from 'design-system';
import { AppView } from 'design-system/AppView';
import EmptyComponent from 'design-system/EmptyComponent';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import StatusBar from 'design-system/StatusBar';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { IconDocument } from 'design-system/icons';
import SectionHeader from 'features/invest/components/SectionHeader';
import FileViewer from 'react-native-file-viewer';
import Modal from 'utils/packages/Modal';
import Text from 'design-system/Text';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useAppDispatch } from 'store/hooks';
import useRefreshAccounts from 'hooks/useRefreshAccounts';
import { getSpaceInfo, getSpaceInfoSuccess } from 'features/spaces/actions';
import { muteAll, useFetchOld } from 'hooks';
import { getAccountGoals, getTransactions } from 'actions';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { isWeb } from '../../../constants';
import { useUploadCsvTransactions } from '../hooks/useUploadCsvTransactions';
import useInvestmentGraphBalanceHistory from '../hooks/useInvestmentGraphBalanceHistory';
const strings = {
    title: 'Is the matching correct?',
    subtitle: 'We are going to import the transactions based on this matching',
    uploadAnother: 'No, let me upload another file',
    yesImport: 'Yes, import transactions',
    sectionTitle: 'Uploaded file',
    successTitle: 'Your transactions have been imported 🎉',
    successButtonText: 'Close',
    error: {
        importError: {
            title: 'Something went wrong',
            description: 'We were unable to import your transactions. Please try again.',
            buttonText: 'Ok',
        },
        previewError: {
            title: 'File preview',
            description: 'File preview not available',
            buttonText: 'Ok',
        },
    },
};
const SQUIRCLE_SIZE = rem(44);
// WORK IN PROGRESS SCREEN
const DummyAccountImportAddTransactionsScreen = withAnimatedNavigationHeader(({ navigation, route, onScroll }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const [, , , fetch] = useFetchOld(muteAll);
    const { applyTransactions } = useUploadCsvTransactions();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const dispatch = useAppDispatch();
    const refreshAccounts = useRefreshAccounts();
    const loadInvestmentGraphBalanceHistory = useInvestmentGraphBalanceHistory();
    const [loading, setLoading] = useState(false);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            ...(isWeb && {
                title: strings.title,
                headerTitle: EmptyComponent,
            }),
        });
    }, [colors.text.primary]);
    const successSubtitle = useMemo(() => (newRecords, ignoredRecords) => `${newRecords} transaction${newRecords === 1 ? '' : 's'} successfully added, and ${ignoredRecords ? `${ignoredRecords}` : 'any'} duplicate${ignoredRecords === 1 ? '' : 's'} ${ignoredRecords === 1 ? 'was' : 'were'} automatically skipped. \n The new balance has been updated and may be off if the file isn’t complete. You can adjust it in settings.`, []);
    const onYesImport = useCallback(async () => {
        const { id } = route.params;
        if (!id) {
            navigation.navigateDeprecated('DummyAccountImportCurrency', { ...route.params });
        }
        else {
            try {
                const { filename, config, spaceId, type } = route.params;
                if (!filename || !config) {
                    throw new Error('Invalid parameters');
                }
                setLoading(true);
                const response = await applyTransactions(id, filename, config, true); // true = mute alert
                if (!response || response.error.length > 0) {
                    throw new Error('Error importing transactions');
                }
                const { newRecords, ignoredRecords } = response.success;
                await fetch(getAccountGoals());
                if (spaceId) {
                    const res = await fetch(getSpaceInfo(spaceId));
                    if (res) {
                        dispatch(getSpaceInfoSuccess(res));
                    }
                }
                if (type) {
                    refreshAccounts(type);
                    if (type === 'INVESTMENT') {
                        loadInvestmentGraphBalanceHistory(id);
                    }
                }
                await fetch(getTransactions({ data: Number(id), page: 1, hasInternalTransactions: false }));
                setLoading(false);
                navigation.navigateDeprecated('Success', {
                    title: strings.successTitle,
                    subTitle: successSubtitle(newRecords, ignoredRecords),
                    buttonText: strings.successButtonText,
                    onContinue: () => {
                        navigation.navigateDeprecated('Transactions', { id });
                        return true;
                    },
                });
            }
            catch {
                setLoading(false);
                Modal.showConfirmation({
                    title: strings.error.importError.title,
                    subtitle: strings.error.importError.description,
                    confirmButtonTitle: strings.error.importError.buttonText,
                });
            }
        }
    }, [applyTransactions, loadInvestmentGraphBalanceHistory, refreshAccounts, route.params, successSubtitle]);
    const onUploadAnother = useCallback(() => navigation.pop(), [navigation]);
    const onPreviewFile = useCallback(async (file) => {
        try {
            if (isWeb && file.file) {
                const url = URL.createObjectURL(file.file);
                const win = window?.open(url, '_blank', 'noopener,noreferrer');
                if (win)
                    win.opener = null;
                setTimeout(() => URL.revokeObjectURL(url), 2000);
                return;
            }
            const { path: filePath, localCopyPath } = file;
            let localPath = filePath;
            if (localCopyPath && typeof localCopyPath === 'string') {
                localPath = localCopyPath;
            }
            const fileUrl = localPath.startsWith('file://') ? localPath : `file://${localPath}`;
            await FileViewer.open(fileUrl, { showAppsSuggestions: true, showOpenWithDialog: true });
        }
        catch {
            Modal.showConfirmation({
                title: strings.error.previewError.title,
                subtitle: strings.error.previewError.description,
                confirmButtonTitle: strings.error.previewError.buttonText,
            });
        }
    }, []);
    const tableRow = ({ column1, column2, isHeader, isLast, }) => (<View style={[styles.tableRowContainer, !isLast && styles.tableRowHorizontalSeparator]}>
          <View style={[styles.tableRow, styles.tableRowVerticalSeparator]}>
            <Text {...(isHeader ? { 'Text-14': true } : { 'TextThin-14': true })}>{column1}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text {...(isHeader ? { 'Text-14': true } : { 'TextThin-14': true })}>{column2}</Text>
          </View>
        </View>);
    const ParseConfigTable = ({ config }) => {
        const isDebitCreditCombined = config.parseConfig.debitAmountIndex === config.parseConfig.creditAmountIndex;
        const tableData = {
            header1: 'Data in the file',
            header2: 'In Emma',
            rows: [
                { column1: config.columns[config.parseConfig.bookingDateColumnIndex]?.label || '', column2: 'Date' },
                { column1: config.columns[config.parseConfig.creditAmountIndex]?.label || '', column2: 'Amount' },
                ...(!isDebitCreditCombined
                    ? [{ column1: config.columns[config.parseConfig.debitAmountIndex]?.label || '', column2: 'Amount' }]
                    : []),
                { column1: config.columns[config.parseConfig.counterpartyColumnIndex]?.label || '', column2: 'Reference' },
            ],
        };
        return (<CardView noMarginTop noMarginBottom>
            {tableRow({
                column1: tableData.header1,
                column2: tableData.header2,
                isHeader: true,
                isLast: false,
            })}
            {tableData.rows.map((row, index) => (<React.Fragment key={index}>
                {tableRow({
                    column1: row.column1,
                    column2: row.column2,
                    isHeader: false,
                    isLast: index === tableData.rows.length - 1,
                })}
              </React.Fragment>))}
          </CardView>);
    };
    const { config, fileInfo } = route.params;
    if (typeof config !== 'object')
        return null;
    return (<AppView withFrame={false} paddingBottom>
          <ScrollView onScroll={onScroll} style={paddingHorizontalStyle} scrollEventThrottle={16}>
            <StatusBar barStyle="dark-content" animated/>
            <ScreenInfoHeader title={strings.title} subTitle={strings.subtitle}/>
            <ParseConfigTable config={config}/>
            <SectionHeader title={strings.sectionTitle}/>
            <TouchableBounceCard noMargin onPress={() => onPreviewFile(fileInfo)}>
              <CardView noMarginTop>
                <SettingsItem title={fileInfo.name} description={<Text TextThin-14 OnBrand>
                      Review
                    </Text>} titleProps={{ numberOfLines: 1 }} renderIcon={() => (<View style={styles.iconContainer}>
                      <View style={styles.iconContainerInner}>
                        <IconDocument color={colors.elements.brand}/>
                      </View>
                    </View>)} 
    // icon={<IconDocument color={colors.elements.brand} />}
    style={styles.containerPadding}/>
              </CardView>
            </TouchableBounceCard>
          </ScrollView>
          <ButtonGroup type="floating">
            <Button brandReversed title={strings.uploadAnother} onPress={onUploadAnother}/>
            <Button brand title={strings.yesImport} onPress={onYesImport} isFetching={loading}/>
          </ButtonGroup>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    container: {
        flex: 1,
    },
    icon: {
        marginRight: rem(16),
    },
    tableRowContainer: {
        flexDirection: 'row',
    },
    tableRowHorizontalSeparator: {
        borderBottomWidth: 1,
        borderColor: colors.background.dark,
    },
    tableRowVerticalSeparator: {
        borderRightWidth: 1,
        borderColor: colors.background.dark,
    },
    tableRow: {
        flex: 1,
        padding: rem(16),
    },
    containerPadding: {
        ...(isWeb && {
            padding: rem(8),
        }),
    },
    iconContainer: {
        width: SQUIRCLE_SIZE,
        height: SQUIRCLE_SIZE,
        borderRadius: rem(12),
        backgroundColor: colors.background.brandLight,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: rem(16),
    },
    iconContainerInner: {
        position: 'absolute',
    },
}));
export default DummyAccountImportAddTransactionsScreen;
